// src/utils/formWidth.js
//
// What the form layout editor says about an element's WIDTH (KPE-023): the
// choices it offers, which of them the element's type allows, and why not for
// the ones it does not. Every number comes from the vendored limits table
// (utils/formGrid/limits — the one the API validates against and the renderer
// draws from), so what the editor offers is what the form can draw.
//
// Pure: no React, no DOM. The controls (components/cards/FormWidthControl) and
// the drag-and-resize editor that follows read the same answers.

import { AUTO, COLUMNS, limitsFor, spanVerdict } from './formGrid/limits';

/** The quick picks, as fractions of the row and the columns they are of 12. */
export const QUICK_SPANS = Object.freeze([
	Object.freeze({ key: 'quarter', label: '1/4', span: 3 }),
	Object.freeze({ key: 'third', label: '1/3', span: 4 }),
	Object.freeze({ key: 'half', label: '1/2', span: 6 }),
	Object.freeze({ key: 'two-thirds', label: '2/3', span: 8 }),
	Object.freeze({ key: 'three-quarters', label: '3/4', span: 9 }),
	Object.freeze({ key: 'full', label: 'Full', span: COLUMNS }),
]);

const FRACTIONS = {
	2: 'a sixth of the row',
	3: 'a quarter of the row',
	4: 'a third of the row',
	6: 'half the row',
	8: 'two thirds of the row',
	9: 'three quarters of the row',
	12: 'the whole row',
};

/** "half the row", or "5 of 12 columns" where no fraction names it. */
export function rowFraction(columns) {
	return FRACTIONS[columns] || `${columns} of ${COLUMNS} columns`;
}

const capital = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/**
 * The limits the editor offers an element, ready to show:
 * `{ kind, label, min, auto, max, buttons }` — `label` the person's name for
 * the type ("rich text"), `buttons` how many options a choice shown as
 * buttons must fit on one row (0 for every other kind).
 *
 * The API and the renderer hold an element to ONE row of the table, its own
 * type's — the two halves of a date range included, each its own field
 * (KPR-626, KPR-600) — so what is offered is what saves and what is drawn.
 */
export function widthLimits(element) {
	const own = limitsFor(element);
	return {
		kind: own.kind,
		label: own.label,
		min: own.min,
		auto: own.auto,
		max: own.max,
		buttons: own.kind === 'segmented' && Array.isArray(element?.options?.choices) ? element.options.choices.length : 0,
	};
}

/** Why a width under the minimum is not offered, in a sentence: "Rich text needs at least half the row". */
export function belowMinimumReason(limits) {
	if (limits.min <= 1) return null;
	const fit = limits.buttons ? ` to fit its ${limits.buttons} buttons on one row` : '';
	const need = limits.min >= COLUMNS ? 'the whole row' : `at least ${rowFraction(limits.min)}`;
	return `${capital(limits.label)} needs ${need}${fit}`;
}

/** Why a width over the maximum is not offered: "Number takes at most half the row". */
export function aboveMaximumReason(limits) {
	if (limits.max >= COLUMNS) return null;
	return `${capital(limits.label)} takes at most ${rowFraction(limits.max)}`;
}

/** The one sentence that says what widths this element can have. */
export function limitsNote(limits) {
	if (limits.min === limits.max) {
		const only = `${capital(limits.label)} takes ${rowFraction(limits.min)}`;
		return limits.auto === limits.min ? `${only}.` : `${only}; Auto is ${rowFraction(limits.auto)}.`;
	}
	const below = belowMinimumReason(limits);
	const above = aboveMaximumReason(limits);
	if (below && above) {
		return `${capital(limits.label)} takes ${limits.min} to ${limits.max} of ${COLUMNS} columns: at least ${rowFraction(limits.min)}, at most ${rowFraction(limits.max)}.`;
	}
	if (below) return `${below}.`;
	if (above) return `${above}.`;
	return `${capital(limits.label)} can take any width from 1 to ${COLUMNS} columns.`;
}

/** Whether `span` is a whole number of columns this element may hold. */
export function spanAllowed(limits, span) {
	return Number.isInteger(span) && span >= limits.min && span <= limits.max;
}

/** Why `span` is not offered, or null when it is. */
export function spanRefusal(limits, span) {
	if (spanAllowed(limits, span)) return null;
	return span < limits.min ? belowMinimumReason(limits) : aboveMaximumReason(limits);
}

/**
 * The choices the width control offers, Auto first then the quick picks:
 * `[{ key, label, span, disabled, reason }]`. Auto is always allowed and says
 * the width it resolves to for this element ("Auto (4 of 12)"); a quick pick
 * outside the limits is disabled with the reason.
 */
export function widthChoices(limits) {
	return [
		{ key: 'auto', label: `Auto (${limits.auto} of ${COLUMNS})`, span: AUTO, disabled: false, reason: null },
		...QUICK_SPANS.map(pick => {
			const reason = spanRefusal(limits, pick.span);
			return { key: pick.key, label: pick.label, span: pick.span, disabled: reason !== null, reason };
		}),
	];
}

/**
 * What a stored `span` is, against the element's limits:
 * `{ auto, span, drawn, fix, flag }` — `drawn` the columns the form draws it
 * at (the renderer's clamp, or the type's Auto), `fix` the nearest width the
 * editor offers, `flag` null when the span is fine, else the sentence the
 * editor shows: what is stored, why it cannot hold, and what is drawn.
 */
export function widthState(element, span) {
	const limits = widthLimits(element);
	const verdict = spanVerdict(element, span);
	const auto = verdict.span === AUTO;
	const drawn = verdict.effective;
	const fix = auto ? limits.auto : Math.min(limits.max, Math.max(limits.min, Number.isInteger(span) ? span : limits.auto));
	let flag = null;
	if (!auto && !spanAllowed(limits, span)) {
		const stored = typeof span === 'string' ? `"${span}"` : String(span);
		const why = !Number.isInteger(span) || span < 1 || span > COLUMNS
			? `is not a whole number from 1 to ${COLUMNS}`
			: span < limits.min ? `is under the minimum of ${limits.min}` : `is over the maximum of ${limits.max}`;
		flag = `The stored width ${stored} ${why}. It is drawn at ${drawn}.`;
	}
	return { auto, span: verdict.span, drawn, fix, flag };
}

/** The row's chip: "Auto · 4 of 12", "6 of 12", with " · new row" when it starts one. */
export function widthSummary(state, newRow = false) {
	const base = state.auto ? `Auto · ${state.drawn} of ${COLUMNS}` : `${state.drawn} of ${COLUMNS}`;
	return newRow ? `${base} · new row` : base;
}
